import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AMENITY_LABEL,
  MODE_SHORT,
  cityGradient,
  initials,
  mad,
  perUnit,
  walkMinutes,
} from '../lib/format';
import type { PropertyCard } from '../lib/types';
import { photoForCity } from '../lib/city-photos';
import { t } from '../lib/i18n';
import { EyeIcon, EyeOffIcon } from './icons';

/** Small shared pieces. Nothing here fetches; they only render what they are given. */

export function Badge({
  children,
  tone = '',
  title,
}: {
  children: ReactNode;
  tone?: string;
  title?: string;
}) {
  return (
    <span className={`badge ${tone}`} title={title}>
      {children}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row" style={{ gap: 8, color: 'var(--ink2)' }}>
      <span className="spinner" aria-hidden />
      {label ? <span className="small">{label}</span> : null}
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
        {t("Chargement…")}
      </span>
    </span>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok';
  children: ReactNode;
}) {
  return <div className={`notice notice--${tone}`}>{children}</div>;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {description ? <p className="small" style={{ maxWidth: 380 }}>{description}</p> : null}
      {action}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      {label ? <span className="field__label">{label}</span> : null}
      {children}
      {error ? <span className="field__error">{error}</span> : null}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  error,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  // A password field carries an eye button, so a typo can be checked before sending.
  const [revealed, setRevealed] = useState(false);
  const isPassword = rest.type === 'password';
  const input = (
    <input
      {...rest}
      type={isPassword && revealed ? 'text' : rest.type}
      className={`input ${error ? 'input--invalid' : ''}`}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    />
  );
  if (!isPassword) return input;
  return (
    <span className="password-field">
      {input}
      <button
        type="button"
        className="password-field__toggle"
        onClick={() => setRevealed((current) => !current)}
        aria-label={revealed ? t('Masquer le mot de passe') : t('Afficher le mot de passe')}
        aria-pressed={revealed}
        title={revealed ? t('Masquer le mot de passe') : t('Afficher le mot de passe')}
      >
        {revealed ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </span>
  );
}

export function NumberInput({
  value,
  onChange,
  suffix,
  ...rest
}: {
  value: number | '';
  onChange: (value: number | '') => void;
  suffix?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <span className="row" style={{ gap: 8 }}>
      <input
        {...rest}
        className="input"
        inputMode="numeric"
        value={value === '' ? '' : String(value)}
        onChange={(event) => {
          const raw = event.target.value.replace(/[^\d]/g, '');
          onChange(raw === '' ? '' : Number(raw));
        }}
      />
      {suffix ? <span className="small muted nowrap">{suffix}</span> : null}
    </span>
  );
}

export function Select({
  value,
  onChange,
  options,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  return (
    <select
      {...rest}
      className="select"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <div className="row row--between">
      <span className="small strong">{label}</span>
      <button
        type="button"
        className={`toggle ${on ? 'toggle--on' : ''}`}
        aria-label={label}
        aria-pressed={on}
        onClick={() => onChange(!on)}
      >
        <span className="toggle__knob" />
      </button>
    </div>
  );
}

export function Avatar({
  name,
  url,
  size = 40,
}: {
  name: string;
  url?: string | null;
  size?: number;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        width={size}
        height={size}
        style={{ borderRadius: '50%', objectFit: 'cover', flex: 'none' }}
      />
    );
  }
  return (
    <span
      aria-hidden
      style={{
        display: 'grid',
        placeItems: 'center',
        width: size,
        height: size,
        flex: 'none',
        borderRadius: '50%',
        background: 'var(--terra-soft)',
        color: 'var(--terra)',
        fontWeight: 800,
        fontSize: size * 0.38,
      }}
    >
      {initials(name)}
    </span>
  );
}

export function Stars({ rating, count }: { rating: number | null; count?: number }) {
  if (rating === null) return <span className="small muted">{t("Pas encore d’avis")}</span>;
  return (
    <span className="row small" style={{ gap: 5 }}>
      <span aria-hidden style={{ color: 'var(--saff)' }}>
        ★
      </span>
      <span className="strong">{rating.toFixed(1)}</span>
      {count !== undefined ? <span className="muted">({count})</span> : null}
    </span>
  );
}

export function AmenityList({ amenities }: { amenities: string[] }) {
  if (amenities.length === 0) return <span className="small muted">{t("Non précisé")}</span>;
  return (
    <div className="row row--wrap" style={{ gap: 8 }}>
      {amenities.map((amenity) => (
        <span key={amenity} className="badge">
          {AMENITY_LABEL[amenity] ?? amenity}
        </span>
      ))}
    </div>
  );
}

export function ListingCard({ listing }: { listing: PropertyCard }) {
  return (
    <Link className="listing-card" to={`/logement/${listing.id}`}>
      <div className="listing-card__media">
        {listing.coverUrl ? (
          <img src={listing.coverUrl} alt="" loading="lazy" />
        ) : null}
        <div className="listing-card__badges">
          {listing.hostVerified ? <Badge tone="badge--verified">{t("Vérifié")}</Badge> : null}
          {listing.visited ? <Badge tone="badge--visited">{t("Visité")}</Badge> : null}
        </div>
      </div>
      <div className="listing-card__body">
        <div className="row" style={{ gap: 6 }}>
          {listing.modes.map((mode) => (
            <span key={mode} className="tiny strong" style={{ color: 'var(--teal)' }}>
              {MODE_SHORT[mode]}
            </span>
          ))}
        </div>
        <span className="listing-card__title">{listing.title}</span>
        <span className="listing-card__meta">
          {listing.neighborhood}, {t(listing.city)}
          {listing.distanceMetres !== null
            ? ` · ${t('{minutes} min à pied du campus', { minutes: walkMinutes(listing.distanceMetres) })}`
            : ''}
        </span>
        <div className="row row--between" style={{ marginTop: 3 }}>
          {listing.priceFrom ? (
            <span className="listing-card__price">
              {mad(listing.priceFrom.priceCentimes)}
              <span>{perUnit(listing.priceFrom.unit)}</span>
            </span>
          ) : (
            <span className="small muted">{t("Prix à venir")}</span>
          )}
          <Stars rating={listing.ratingAverage} count={listing.ratingCount || undefined} />
        </div>
      </div>
    </Link>
  );
}

export function CityTile({
  city,
  count,
  onClick,
}: {
  city: string;
  count: number;
  onClick: () => void;
}) {
  const photo = photoForCity(city);
  // The gradient stays underneath: it is what shows while the photo loads, and
  // the whole tile if a city is ever added without one.
  return (
    <button type="button" className="city-tile" style={{ background: cityGradient(city) }} onClick={onClick}>
      {photo ? (
        <>
          <img
            className="city-tile__photo"
            src={photo.src}
            // The name is written on the tile; repeating it here would make a
            // screen reader say it twice.
            alt=""
            loading="lazy"
            decoding="async"
            width={640}
            height={480}
          />
          <span className="city-tile__shade" />
        </>
      ) : (
        <span className="city-tile__pattern" />
      )}
      <span className="city-tile__label">
        <span style={{ display: 'block', fontWeight: 800, fontSize: 15.5 }}>{t(city)}</span>
        <span style={{ fontSize: 12, opacity: 0.92 }}>
          {/* "0 logements" on every tile reads as a broken site. A city with
              nothing in it yet is open for listings, which is the true and more
              useful thing to say. */}
          {count > 0 ? t('{count} logement(s)', { count }) : t("Ouvert aux annonces")}
        </span>
      </span>
    </button>
  );
}

export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="sheet__panel" onClick={(event) => event.stopPropagation()}>
        <div className="row row--between" style={{ marginBottom: 14 }}>
          <h3>{title}</h3>
          <button type="button" className="btn btn--quiet btn--sm" onClick={onClose} aria-label={t("Fermer")}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Two-field date range with a live nights/months readout. */
export function DateRange({
  start,
  end,
  onStart,
  onEnd,
  startLabel = t("Arrivée"),
  endLabel = t("Départ"),
  min,
}: {
  start: string;
  end: string;
  onStart: (value: string) => void;
  onEnd: (value: string) => void;
  startLabel?: string;
  endLabel?: string;
  min?: string;
}) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
      <Field label={startLabel}>
        <input
          className="input"
          type="date"
          value={start}
          min={min}
          onChange={(event) => onStart(event.target.value)}
        />
      </Field>
      <Field label={endLabel}>
        <input
          className="input"
          type="date"
          value={end}
          min={start || min}
          onChange={(event) => onEnd(event.target.value)}
        />
      </Field>
    </div>
  );
}

/** Copy-to-clipboard for RIBs and payment codes. */
export function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="row row--between" style={{ gap: 10 }}>
      <span className="small">
        <span className="muted">{label} · </span>
        <span className="strong">{value}</span>
      </span>
      <button
        type="button"
        className="btn btn--quiet btn--sm"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        }}
      >
        {copied ? t("Copié") : t("Copier")}
      </button>
    </div>
  );
}
